import { useMemo, useState, useCallback, useEffect } from 'react';
import { WorkflowStepper } from '../../../components/WorkflowStepper';
import { useBanner } from '../../../layouts/banner';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { fmtLocalDate } from '../../../utils/datetime';
import { LjChart, OverlayChart, type OverlaySeries, type Pt } from './ControlChartSvg';
import {
  download, xlsOf, svgToPng, popoutChart, defRows, stem,
  RESULT_COLS, STAT_COLS, DEF_COLS,
} from './controlChartExport';
import { ReportDialog } from './ControlChartReport';
import './controlChart.css';
import {
  OP_TONE, THEMES, SIG_MEANINGS, SIG_ERRS, RULE_TEXT,
  sig, valueOf, valueShort, outOfControlCount, sigHash,
  type Field, type QcRow, type SavedChart, type Signature, type Stat, type TestDef,
} from './controlChartData';
import {
  fetchSavedCharts, fetchTests, fetchFacets, retrieve as apiRetrieve,
  fetchSavedDetail, fetchSavedPoints, saveChart, deleteChart, fetchAudit,
  type ApiAuditRow, type ApiFacets, type ApiScope,
} from './controlChartApi';

// ─────────────────────────────────────────────────────────────────────────────
// QA/QC → Control Charts
//
// The saved-chart list, the query builder (test lookup, instrument / analyst /
// date filters, QC role, QC type and parameter pickers), the QC results and
// statistics grids, and the chart workbench — Levey-Jennings charts, the
// z-score overlay, the rule evaluation, the electronic signature, the report
// preview, the mini-SOP guide and the audit trail.
//
// Runs on the LIMS's OWN QC results (dbo.SampleParameter), through
// /api/v1/control-charts.  The statistics, the control limits, the charted
// column and the out-of-control counts are all computed by the API — nothing on
// this screen invents a number (CLAUDE.md §19.1).  Saving mints a race-safe
// CCID, claims the QC results it charts, and records the electronic signature
// (§14a Rule B, ADR-0009); every action is audited server-side by
// IAuditTrailService (§14a Rule A).
//
// Rendered with the shared components (FormTitle / AppToolbar / AppDataGrid /
// GridSearch / useNotify / useConfirm) so it sits alongside every other form
// (standing rules 7, 8 and 9).  Only what those don't own — the query panel, the
// chart canvas and the signature block — is in controlChart.css, token-driven.
//
// Dates render through the shared fmtLocal* helpers, never inline (§14b.6 / C2).
// ─────────────────────────────────────────────────────────────────────────────

type View = 'list' | 'query';
type Tab = 'results' | 'chart';

/** The six steps of building a chart, shown as a stepper above the form. `done`
 *  is derived from the state itself rather than a counter, so going back a step
 *  can never leave the stepper claiming progress that was undone. */
function steps(test: TestDef | null, qcTypes: string[], params: string[], stats: Stat[], tab: Tab) {
  return [
    { l: 'Select a Test', done: !!test },
    { l: 'Filters', done: !!test },
    { l: 'QC Type', done: qcTypes.length > 0 },
    { l: 'Parameter', done: params.length > 0 },
    { l: 'Load', done: stats.length > 0 },
    { l: 'Chart', done: tab === 'chart' && stats.length > 0 },
  ];
}

const BANDS = [
  'Pick the test to chart. Every row shows how many QC records sit behind it.',
  'Narrow by instrument, analyst and date. Blank means all.',
  'Choose a QC role, then tick the QC types to chart.',
  'Tick the parameters to include.',
  'Click Load to build the data set.',
  'Step through each parameter with the arrows.',
];

const EMPTY_FACETS: ApiFacets = {
  totalRecords: 0, minDate: null, maxDate: null,
  roles: [], qcTypes: [], parameters: [], instruments: [], analysts: [],
};

const dash = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));

/** Turns an axios failure into the message the API actually sent, so the user
 *  sees "Password is incorrect" rather than "Request failed with status 400". */
const apiMessage = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: { message?: string } } } })
    ?.response?.data?.error?.message ?? fallback;

export default function ControlChartPage() {
  const notify = useNotify();
  const confirm = useConfirm();

  // ── list state ────────────────────────────────────────────────────────────
  const [saved, setSaved] = useState<SavedChart[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listMs, setListMs] = useState<number | null>(null);

  // ── query state ───────────────────────────────────────────────────────────
  const [view, setView] = useState<View>('list');
  const [tab, setTab] = useState<Tab>('results');
  const [tests, setTests] = useState<TestDef[]>([]);
  const [test, setTest] = useState<TestDef | null>(null);
  const [search, setSearch] = useState('');
  const [lookupOpen, setLookupOpen] = useState(false);
  const [inst, setInst] = useState<string[]>([]);
  const [anly, setAnly] = useState<string[]>([]);
  const [instOpen, setInstOpen] = useState(false);
  const [anlyOpen, setAnlyOpen] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [role, setRole] = useState('');
  const [qcTypes, setQcTypes] = useState<string[]>([]);
  const [params, setParams] = useState<string[]>([]);
  const [facets, setFacets] = useState<ApiFacets>(EMPTY_FACETS);
  const [rows, setRows] = useState<QcRow[]>([]);
  const [stats, setStats] = useState<Stat[]>([]);
  const [field, setField] = useState<Field>('rec');
  const [dropped, setDropped] = useState<{ lost: number; field: Field; types: string[] } | null>(null);
  const [chartIdx, setChartIdx] = useState(0);
  const [chartMode, setChartMode] = useState<'single' | 'overlay'>('single');
  const [hidden, setHidden] = useState<string[]>([]);
  const [theme, setTheme] = useState('light');
  const [ccid, setCcid] = useState('');
  const [chartOid, setChartOid] = useState('');
  const [signature, setSignature] = useState<Signature | null>(null);
  const [queryMs, setQueryMs] = useState<number | null>(null);

  // ── dialogs ───────────────────────────────────────────────────────────────
  const [signOpen, setSignOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState<string | null | false>(false);
  const [reportOpen, setReportOpen] = useState(false);

  const th = THEMES[theme] ?? THEMES.light!;

  /** The selection, in the ONE shape the facets / Load / Save calls all take. */
  const scope: ApiScope = useMemo(() => ({
    testMethodId: test?.testMethodId ?? '',
    from: from || null,
    to: to || null,
    instruments: inst,
    analysts: anly,
    role: role || null,
    qcTypes,
    parameters: params,
  }), [test, from, to, inst, anly, role, qcTypes, params]);

  const roles = facets.roles;
  const qcs = facets.qcTypes;
  const pars = facets.parameters;

  // ── data loading ──────────────────────────────────────────────────────────

  const reloadList = useCallback(async (signal?: AbortSignal) => {
    const t0 = performance.now();
    setListLoading(true);
    try {
      setSaved(await fetchSavedCharts(signal));
      setListMs(performance.now() - t0);
    } catch (e) {
      if (!signal?.aborted) notify.error(apiMessage(e, 'The saved control charts could not be loaded.'));
    } finally {
      if (!signal?.aborted) setListLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    const ac = new AbortController();
    void reloadList(ac.signal);
    fetchTests(ac.signal)
      .then(list => setTests(list.map(t => ({
        testMethodId: t.testMethodId,
        matrix: t.matrix ?? '',
        test: t.test ?? '',
        method: t.method ?? '',
        records: t.records,
      }))))
      .catch(e => { if (!ac.signal.aborted) notify.error(apiMessage(e, 'The test list could not be loaded.')); });
    return () => ac.abort();
    // Mount only: the list is refreshed explicitly after a save / delete.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Cascading counts. One round trip per change of the selection — the counts
   *  ARE the data (how many QC records sit behind each choice), so they cannot
   *  be derived on the client any more. */
  useEffect(() => {
    if (!test) { setFacets(EMPTY_FACETS); return; }
    const ac = new AbortController();
    fetchFacets(scope, ac.signal)
      .then(f => {
        if (ac.signal.aborted) return;
        setFacets(f);
        // Land on data: when the window has not been set yet, adopt the range
        // the test's OWN QC results span, so Load never opens on an empty range
        // the user has to discover.
        if (!from && !to && f.minDate && f.maxDate) { setFrom(f.minDate); setTo(f.maxDate); }
      })
      .catch(e => {
        if (ac.signal.aborted) return;
        setFacets(EMPTY_FACETS);
        notify.error(apiMessage(e, 'The query panel could not be refreshed.'));
      });
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, test]);

  // ── dynamic island ────────────────────────────────────────────────────────
  // The SHARED <WorkflowStepper> docked in the shell's top-bar slot (BO UI
  // Standard §5) — the same mechanism MDL Studies uses.  Cleared on the list
  // view and on unmount so it cannot outlive the page.
  const { setTopbarCenter } = useBanner();
  const stepList = useMemo(
    () => steps(test, qcTypes, params, stats, tab),
    [test, qcTypes, params, stats, tab]);
  const activeStep = stepList.findIndex(s => !s.done);
  const islandIdx = activeStep < 0 ? stepList.length - 1 : activeStep;

  useEffect(() => {
    if (view !== 'query') { setTopbarCenter(null); return; }
    setTopbarCenter(
      <WorkflowStepper steps={stepList.map(s => ({ label: s.l }))} activeIdx={islandIdx} />);
    return () => setTopbarCenter(null);
  }, [view, stepList, islandIdx, setTopbarCenter]);

  // ── actions ───────────────────────────────────────────────────────────────

  const resetQuery = useCallback(() => {
    setTab('results'); setTest(null); setLookupOpen(false); setSearch('');
    setInst([]); setAnly([]); setInstOpen(false); setAnlyOpen(false);
    setFrom(''); setTo(''); setRole(''); setQcTypes([]); setParams([]);
    setFacets(EMPTY_FACETS);
    setRows([]); setStats([]); setField('rec'); setDropped(null); setChartIdx(0);
    setChartMode('single'); setHidden([]); setSignature(null); setCcid(''); setChartOid('');
    setQueryMs(null);
  }, []);

  /** Choosing a test resets everything downstream and clears the window, which
   *  the facets effect then refills from THAT test's own data. */
  const pickTest = useCallback((id: string) => {
    const t = tests.find(x => x.testMethodId === id) || null;
    setTest(t); setLookupOpen(false); setSearch('');
    setInst([]); setAnly([]); setRole(''); setQcTypes([]); setParams([]);
    setRows([]); setStats([]); setDropped(null);
    setFrom(''); setTo('');
  }, [tests]);

  const retrieve = useCallback(async (over?: Partial<ApiScope>) => {
    const s: ApiScope = { ...scope, ...over };
    if (!s.testMethodId) return;
    const t0 = performance.now();
    try {
      const r = await apiRetrieve(s);
      setRows(r.rows);
      setStats(r.stats);
      setField(r.field);
      setDropped(r.dropped);
      setChartIdx(0);
      setQueryMs(performance.now() - t0);
      // NEVER drop rows silently.  A QC type with no value in the charted field
      // would otherwise just vanish and the counts would stop adding up — the
      // API reports what it left out and the banner below says so.
    } catch (e) {
      notify.error(apiMessage(e, 'The QC results could not be loaded.'));
    }
  }, [scope, notify]);

  const newChart = () => { resetQuery(); setView('query'); };

  /** Reopens a saved chart: its definition, then the points it actually holds —
   *  read back by chart id with the limits stored at signing, so what is shown
   *  is what was signed rather than a fresh re-query. */
  const openSaved = useCallback(async (id?: string) => {
    const oid = id ?? selected;
    if (!oid) return;
    resetQuery();
    setView('query');
    try {
      const detail = await fetchSavedDetail(oid);
      const t: TestDef | null = detail.testMethodId
        ? tests.find(x => x.testMethodId === detail.testMethodId) ?? {
          testMethodId: detail.testMethodId,
          matrix: detail.header.matrix ?? '',
          test: detail.header.test ?? '',
          method: detail.header.method ?? '',
          records: 0,
        }
        : null;
      setTest(t);
      setChartOid(oid);
      setCcid(detail.header.ccid);
      setSignature(detail.header.sig ?? null);
      setFrom(detail.header.from ?? '');
      setTo(detail.header.to ?? '');
      setRole(detail.role ?? '');
      setQcTypes(detail.qcTypes);
      setParams(detail.parameters);

      const t0 = performance.now();
      const pts = await fetchSavedPoints(oid);
      setRows(pts.rows);
      setStats(pts.stats);
      setField(pts.field);
      setDropped(pts.dropped);
      setQueryMs(performance.now() - t0);
    } catch (e) {
      notify.error(apiMessage(e, 'That control chart could not be opened.'));
      setView('list');
    }
  }, [selected, resetQuery, tests, notify]);

  const askDelete = async () => {
    const c = saved.find(x => x.oid === selected);
    if (!c) return;
    const ok = await confirm.confirm({
      title: 'Delete',
      message: `Delete control chart ${c.ccid} (${c.test} · ${c.qc})? `
        + 'The QC results it holds are released and can be charted again.',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteChart(c.oid);
      setSelected(null);
      await reloadList();
      notify.success('Deleted.');
    } catch (e) {
      notify.error(apiMessage(e, 'The control chart could not be deleted.'));
    }
  };

  const askSave = async () => {
    if (!stats.length) return;
    if (chartOid) {
      notify.warning('This chart is already saved and signed. Use New to build another.');
      return;
    }
    const ok = await confirm.confirm({
      title: 'Save',
      message: `Do you want to Save? ${stats.length} parameter(s), ${rows.length.toLocaleString()} QC results, `
        + `${fmtLocalDate(from)} – ${fmtLocalDate(to)}.`,
    });
    if (!ok) return;
    setSignOpen(true);
  };

  /** A saved chart IS a QC record — it fixes the limits routine QC is judged
   *  against — so it is signed, not merely saved (ADR-0009).  The password and
   *  the 6-digit code are verified by the API and never persisted; the CCID is
   *  minted there too, race-safely (§14a Rule B).
   *
   *  Returns the API's message on failure so the dialog can stay open and show
   *  it, rather than closing over a signature that was never applied. */
  const doSave = async (s: { meaning: string; comment: string; password: string; code: string }) => {
    if (!test) return 'No test selected.';
    try {
      const res = await saveChart(scope, {
        meaning: s.meaning,
        comment: s.comment || null,
        password: s.password,
        code: s.code,
        certified: true,
      });
      setSignOpen(false);
      setCcid(res.ccid);
      setChartOid(res.oid);
      setSignature(res.row.sig ?? null);
      setSelected(res.oid);
      await reloadList();
      setView('list');
      // Say so when the chart took fewer points than the screen showed: another
      // chart claimed some between the Load and the Save.
      if (res.claimed < rows.length) {
        notify.warning(`Chart ${res.ccid} saved with ${res.claimed.toLocaleString()} of `
          + `${rows.length.toLocaleString()} results — the rest had already been claimed by another chart.`);
      } else {
        notify.success(`Chart ${res.ccid} signed and saved successfully.`);
      }
      return null;
    } catch (e) {
      return apiMessage(e, SIG_ERRS.bad!);
    }
  };

  // ── export ────────────────────────────────────────────────────────────────
  // Real files, not a success message. An export that reports success without
  // writing anything is worse than no export: the user believes they have the
  // data and finds out later that they do not.

  const exportDefn = () => defRows({
    ccid, test, field, qcTypes, params, from, to, inst, anly, role,
    nRows: rows.length, nParams: stats.length,
    signedBy: signature?.by, hash: signature?.hash,
  });

  const doChartExport = async (what: string) => {
    const base = stem(test, qcTypes, from, to);
    try {
      if (what === 'Image') {
        const svg = document.querySelector<SVGSVGElement>('.cc-chart');
        if (!svg) { notify.warning('There is no chart on screen to export.'); return; }
        await svgToPng(svg, `${base}_chart.png`);
      } else if (what === 'Excel') {
        // The three-sheet workbook: the points, the limits that judge them, and
        // the definition needed to reproduce the run.
        download(`${base}_QC.xls`, xlsOf([
          { name: 'QC Results', cols: RESULT_COLS, rows },
          { name: 'Statistics', cols: STAT_COLS, rows: stats },
          { name: 'Definition', cols: DEF_COLS, rows: exportDefn() },
        ] as never[]), 'application/vnd.ms-excel');
      } else {
        // PDF — the browser's own "Save as PDF" over a clean window.
        const ok = doPopout(true);
        if (!ok) return;
      }
    } catch (e) {
      notify.error(`The ${what} export failed: ${e instanceof Error ? e.message : 'unknown error'}`);
      return;
    }
    notify.success(`Exported to ${what} successfully.`);
  };

  const doPopout = (print = false) => {
    const head = `${test?.test ?? 'Control Chart'} — ${chartMode === 'overlay' ? 'All parameters' : (stats[chartIdx]?.parameter ?? '')}`;
    const sub = [test?.matrix, test?.method, `QCType(s): ${qcTypes.join(', ') || 'all'}`,
      from && to ? `${fmtLocalDate(from)} – ${fmtLocalDate(to)}` : ''].filter(Boolean).join(' · ');
    const ok = popoutChart(
      [document.querySelector('.cc-statstrip'), document.querySelector('.cc-plotwrap'),
        document.querySelector('.cc-sigstrip')],
      head, sub, print);
    if (!ok) {
      notify.warning('The browser stopped the preview window from opening. Allow pop-ups for this page, then try again.');
    }
    return ok;
  };

  /** Fingerprint of the data set currently loaded, for comparison with the one
   *  captured at signing. Re-query a signed chart over a different window or QC
   *  type and the signature reads as stale rather than silently appearing to
   *  cover data it never saw. */
  const curHash = () => sigHash({
    test: test?.test, method: test?.method, qc: qcTypes.join(', ') || 'all',
    from, to, params: stats.length, points: rows.length,
  });

  // ── list view ─────────────────────────────────────────────────────────────

  const savedColumns: AppColumn<SavedChart>[] = [
    { field: 'ccid', header: 'Chart ID', width: 96, render: r => <b className="cc-mono">{r.ccid}</b> },
    { field: 'matrix', header: 'Matrix', width: 96 },
    { field: 'test', header: 'Test', width: 128 },
    { field: 'method', header: 'Method', width: 148 },
    { field: 'qc', header: 'QC Type', width: 96 },
    { field: 'from', header: 'Date From', width: 106, render: r => fmtLocalDate(r.from) },
    { field: 'to', header: 'Date To', width: 106, render: r => fmtLocalDate(r.to) },
    { field: 'params', header: 'Parameters', width: 96, type: 'number' },
    { field: 'by', header: 'Created By', width: 120 },
    { field: 'on', header: 'Created', width: 106, render: r => fmtLocalDate(r.on) },
    {
      field: 'sig', header: 'Signature', width: 106,
      render: r => r.sig
        ? <span className="cc-pill ok" title={`${r.sig.by} · ${r.sig.at} — ${r.sig.meaning}`}>✎ Signed</span>
        : <span className="cc-pill mute">Unsigned</span>,
    },
    {
      field: 'status', header: 'Status', width: 118, type: 'status',
      render: r => (
        <span className={`cc-pill ${r.status === 'In control' ? 'ok'
          : r.status === 'Warning' ? 'warn'
            : r.status === 'Unknown' ? 'mute' : 'bad'}`}>
          {r.status}
        </span>
      ),
    },
  ];

  if (view === 'list') {
    return (
      <div className="cc-wrap">
        <div className="cc-crumb">Home <span>›</span> QA/QC <span>›</span> <b>Control Charts</b></div>
        {/* The audit chip stays HERE rather than moving into <AppToolbar
            auditFormName>: this form's trail spans TWO form names ("Control
            Chart" for the chart and "Control Chart Signing" for the signature),
            and the shared chip queries one. It reads the same
            /api/v1/audit/trail rows the Data Center log shows. */}
        <div className="cc-titlerow">
          <FormTitle pageId="control-charts" loadMs={listMs ?? undefined} loading={listLoading} />
          <button type="button" className="cc-chip" onClick={() => setAuditOpen(null)}>
            🛡 Audit Trail ›
          </button>
        </div>
        <div className="cc-sub">Saved control chart runs. Select one to open, or start a new chart.</div>
        <div className="cc-band">
          <b>Step 1 · Choose a chart.</b> Select a saved run and click <span className="cc-k">Open</span>,
          or click <span className="cc-k">New</span> to build one.
        </div>

        <AppToolbar
          onNew={newChart}
            onEdit={() => void openSaved()}
            editDisabled={!selected}
            newLabel="New"
            onDelete={askDelete}
            deleteDisabled={!selected}
            onPreview={() => {
              /* From the list there is no data set in memory yet — open the
                 saved chart, which loads it, then preview. Previewing an empty
                 state would be a bug wearing a button. */
              if (!selected) return;
              void openSaved().then(() => setReportOpen(true));
            }}
            previewDisabled={!selected}
            onRefresh={() => void reloadList()}
          beforeRefresh={
            <button type="button" className="ca-btn" onClick={() => setGuideOpen(true)}>
              <i className="ti ti-help" />Guide
            </button>
          }
          exportData={{ filename: 'control-charts', ...gridExportMatrix(saved, savedColumns) }}
        />
        <div className="cc-card cc-card-flex cc-listfill">
          <AppDataGrid<SavedChart>
            rows={saved}
            columns={savedColumns}
            getRowId={r => r.oid}
            selectedRowId={selected ?? undefined}
            onRowClick={r => setSelected(r.oid)}
            onRowDoubleClick={r => { setSelected(r.oid); void openSaved(r.oid); }}
            loading={listLoading}
            emptyText="No saved control charts yet. Use New to build one."
            persistKey="control-chart-list"
          />
        </div>

        {guideOpen ? <GuideDialog onClose={() => setGuideOpen(false)} /> : null}
        {/* The audit dialog is mounted on BOTH views — the list has its own
            Audit Trail chip (scoped to every chart), and rendering it only in
            the query view left that chip setting state that nothing showed. */}
        {auditOpen !== false ? (
          <AuditDialog srcId={auditOpen} onClose={() => setAuditOpen(false)} />
        ) : null}
      </div>
    );
  }

  // ── query view ────────────────────────────────────────────────────────────

  const st = stats[chartIdx];
  const bandIdx = activeStep < 0 ? 5 : activeStep;

  const pts: Pt[] = st
    ? rows.filter(r => r.p === st.parameter && Number.isFinite(valueOf(r, field) as number))
      .map(r => ({ v: valueOf(r, field) as number, d: r.d }))
    : [];

  const overlay = chartMode === 'overlay';
  const visible = stats.length - hidden.length;

  /** Of the QC types the API had to drop rows from, the ones that contributed
   *  NOTHING to the plot. Only those are worth deselecting — a type that lost 7
   *  of 592 results is doing its job, and telling the user to remove it would
   *  take the other 585 with it. */
  const unusableTypes = dropped
    ? dropped.types.filter(t => !rows.some(r => r.qt === t))
    : [];

  const overlaySeries: OverlaySeries[] = stats.map((s, i) => ({
    name: s.parameter,
    col: th.pal[i % th.pal.length]!,
    dash: th.dash ? th.dash[i % th.dash.length] : '',
    pts: rows.filter(r => r.p === s.parameter && Number.isFinite(valueOf(r, field) as number))
      .map(r => ({
        z: s.std ? ((valueOf(r, field) as number) - s.mean!) / s.std : 0,
        d: r.d,
        v: valueOf(r, field) as number,
      })),
  })).filter(s => s.pts.length);

  const testList = tests.filter(t => {
    const q = search.trim().toLowerCase();
    return !q || `${t.matrix} ${t.test} ${t.method}`.toLowerCase().includes(q);
  });

  const resultColumns: AppColumn<QcRow>[] = [
    { field: 'd', header: 'Analyzed', width: 104, render: r => fmtLocalDate(r.d) },
    { field: 'p', header: 'Parameter', width: 168 },
    { field: 'rec', header: '%Rec', width: 84, type: 'number', render: (r: QcRow) => dash(r.rec) },
    { field: 'res', header: 'Result', width: 84, type: 'number', render: (r: QcRow) => dash(r.res) },
    { field: 'rpd', header: 'RPD', width: 78, type: 'number', render: (r: QcRow) => dash(r.rpd) },
    { field: 'batch', header: 'QC Batch', width: 152 },
    { field: 'qt', header: 'QC Type', width: 88 },
    { field: 'sample', header: 'Sample', width: 124 },
    { field: 'inst', header: 'Instrument', width: 116 },
    { field: 'anly', header: 'Analyst', width: 116 },
  ];

  const statColumns: AppColumn<Stat>[] = [
    { field: 'parameter', header: 'Parameter', width: 176, render: s => <b>{s.parameter}</b> },
    { field: 'mean', header: 'Mean', width: 92, type: 'number', render: s => sig(s.mean) },
    { field: 'std', header: 'STD', width: 92, type: 'number', render: s => sig(s.std) },
    { field: 'ucl', header: 'UCL', width: 92, type: 'number', render: s => sig(s.ucl) },
    { field: 'uwl', header: 'UWL', width: 92, type: 'number', render: s => sig(s.uwl) },
    { field: 'lwl', header: 'LWL', width: 92, type: 'number', render: s => sig(s.lwl) },
    { field: 'lcl', header: 'LCL', width: 92, type: 'number', render: s => sig(s.lcl) },
    { field: 'count', header: 'Count', width: 78, type: 'number' },
    {
      // Distinct field from the Parameter column above — AppDataGrid keys
      // columns by `field`, and a duplicate key makes React drop a cell.
      field: 'kind', header: 'Status', width: 118, type: 'status',
      render: s => {
        const o = outOfControlCount(rows, s, field);
        return <span className={`cc-pill ${o ? 'bad' : 'ok'}`}>{o ? `${o} out` : 'In control'}</span>;
      },
    },
  ];

  return (
    <div className="cc-wrap">
      <div className="cc-crumb">
        Home <span>›</span> QA/QC <span>›</span>
        <a href="#" onClick={e => { e.preventDefault(); setView('list'); }}>Control Charts</a>
        <span>›</span> <b>{ccid || (test ? test.test : 'New')}</b>
      </div>
      <div className="cc-titlerow">
        <FormTitle
          pageId="control-charts"
          title={`Control Chart ${test ? `– ${test.test}` : '– New'}${ccid ? ` · ${ccid}` : ''}`}
          loadMs={queryMs ?? undefined}
        />
        <button type="button" className={`cc-chip${chartOid ? '' : ' off'}`} disabled={!chartOid}
          title={chartOid ? `Open the audit trail for chart ${ccid}` : 'The audit trail opens once this chart is saved'}
          onClick={() => setAuditOpen(chartOid)}>
          🛡 Audit Trail {chartOid ? '›' : ''}
        </button>
      </div>
      <div className="cc-sub">
        {test
          ? `${test.matrix} · ${test.test} · ${test.method} — ${facets.totalRecords.toLocaleString()} QC records in range`
          : 'Build a control chart from QC results.'}
      </div>

      <div className="cc-band">
        <b>Step {bandIdx + 1} · {stepList[bandIdx]!.l}.</b> {BANDS[bandIdx]}
      </div>

      <AppToolbar
        left={
          <>
            <button type="button" className="ca-btn" disabled={!stats.length} onClick={() => setTab('chart')}>
              <i className="ti ti-chart-line" />Chart
            </button>
            <button type="button" className="ca-btn" disabled={!stats.length} onClick={askSave}>
              <i className="ti ti-device-floppy" />Save
            </button>
          </>
        }
        beforeRefresh={
          <button type="button" className="ca-btn" onClick={() => setGuideOpen(true)}>
            <i className="ti ti-help" />Guide
          </button>
        }
        onPreview={() => (stats.length
          ? setReportOpen(true)
          : notify.warning('Load a data set first — there is no chart to report on yet.'))}
        previewDisabled={!stats.length}
        onRefresh={() => void retrieve()}
        refreshDisabled={!test}
        exportData={{ filename: 'control-chart-results', ...gridExportMatrix(rows, resultColumns) }}
        right={
          <>
            {/* `app-toolbar__spacer` (margin-left:auto) pushes this button and
                everything after it — Load · Reset · Back to list — hard to the
                RIGHT edge of the bar, away from the left action cluster. */}
            <button type="button" className="ca-btn ca-btn-solid app-toolbar__spacer" disabled={!test} onClick={() => void retrieve()}>
              <i className="ti ti-refresh" />Load
            </button>
            <button type="button" className="ca-btn" onClick={resetQuery}>
              <i className="ti ti-rotate" />Reset
            </button>
            <button type="button" className="ca-btn" onClick={() => setView('list')}>
              ← Back to list
            </button>
          </>
        }
      />

      <div className="cc-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'results'}
          className={tab === 'results' ? 'on' : ''} onClick={() => setTab('results')}>
          QC Sample Results
        </button>
        <button type="button" role="tab" aria-selected={tab === 'chart'}
          className={tab === 'chart' ? 'on' : ''}
          disabled={!stats.length}
          onClick={() => stats.length && setTab('chart')}>
          Data Set and Control Chart
        </button>
      </div>

      {tab === 'results' ? (
        <div className="cc-split">
          {/* ── query panel ─────────────────────────────────────────────── */}
          <div className="cc-card cc-panel">
            <div className="cc-frow">
              <label htmlFor="cc-test">Test:<span className="cc-star">*</span></label>
              <div className="cc-lk">
                <button id="cc-test" type="button" className="cc-lkb req"
                  onClick={() => { setLookupOpen(o => !o); setInstOpen(false); setAnlyOpen(false); }}>
                  <span className={test ? '' : 'cc-ph'}>
                    {test ? `${test.matrix}, ${test.test}, ${test.method}` : 'Select a test…'}
                  </span>
                  <i className="ti ti-chevron-down" />
                </button>
                {lookupOpen ? (
                  <div className="cc-pop wide">
                    <div className="cc-lksearch">
                      <GridSearch value={search} onChange={setSearch} placeholder="Search matrix, test or method…" />
                    </div>
                    <div className="cc-lkh"><span>Matrix</span><span>Test</span><span>Method</span><span className="r">Records</span></div>
                    <div className="cc-lkbody">
                      {testList.length ? testList.map(t => (
                        <button key={t.testMethodId} type="button" className="cc-lkr" onClick={() => pickTest(t.testMethodId)}>
                          <span>{t.matrix}</span><span>{t.test}</span><span>{t.method}</span>
                          <span className="r cc-mono">{t.records.toLocaleString()}</span>
                        </button>
                      )) : <div className="cc-none">No matching tests.</div>}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <MultiPick label="Instrument:" options={facets.instruments} values={inst} onChange={setInst}
              open={instOpen} setOpen={o => { setInstOpen(o); setLookupOpen(false); setAnlyOpen(false); }}
              disabled={!test} allLabel="N/A" />
            <MultiPick label="Analyst:" options={facets.analysts} values={anly} onChange={setAnly}
              open={anlyOpen} setOpen={o => { setAnlyOpen(o); setLookupOpen(false); setInstOpen(false); }}
              disabled={!test} allLabel="" />

            <div className="cc-frow">
              <label htmlFor="cc-from">Date From:</label>
              <input id="cc-from" type="date" className="cc-in" value={from} disabled={!test}
                onChange={e => setFrom(e.target.value)} />
            </div>
            <div className="cc-frow">
              <label htmlFor="cc-to">Date To:</label>
              <input id="cc-to" type="date" className="cc-in" value={to} disabled={!test}
                onChange={e => setTo(e.target.value)} />
            </div>
            <div className="cc-frow">
              <label htmlFor="cc-role">QC Role:<span className="cc-star">*</span></label>
              <select id="cc-role" className="cc-in req" disabled={!test} value={role}
                onChange={e => { setRole(e.target.value); setQcTypes([]); setParams([]); }}>
                <option value="">N/A (all roles)</option>
                {roles.map(r => <option key={r.value} value={r.value}>{r.value} ({r.count.toLocaleString()})</option>)}
              </select>
            </div>

            <CheckGrid
              cols={['QC Type', 'QC Role', 'Root Role']}
              template="30px 92px minmax(0,1fr) minmax(0,1fr) 62px"
              rows={qcs.map(q => ({
                id: q.value,
                cells: [q.value, q.role ?? '', q.rootRole ?? ''],
                n: q.count,
              }))}
              sel={qcTypes}
              onToggle={id => { setQcTypes(v => v.includes(id) ? v.filter(x => x !== id) : [...v, id]); setParams([]); }}
              onAll={on => { setQcTypes(on ? qcs.map(q => q.value) : []); setParams([]); }}
            />
            <CheckGrid
              cols={['Parameter']}
              template="30px minmax(0,1fr) 62px"
              rows={pars.map(p => ({ id: p.value, cells: [p.value], n: p.count }))}
              sel={params}
              onToggle={id => setParams(v => v.includes(id) ? v.filter(x => x !== id) : [...v, id])}
              onAll={on => setParams(on ? pars.map(p => p.value) : [])}
            />
          </div>

          {/* ── results + statistics ────────────────────────────────────── */}
          <div className="cc-rightcol">
            <div className="cc-card cc-card-flex">
              <div className="cc-card-t">
                QC sample results
                <span className="cc-note">{rows.length.toLocaleString()} rows · charting {valueShort(field)}</span>
              </div>
              {dropped ? (
                <div className="cc-warnbar">
                  ⚠ <b>{dropped.lost.toLocaleString()} row(s) not charted.</b>{' '}
                  {unusableTypes.length ? (
                    /* The QC type carries this statistic on NONE of its results —
                       a blank has no recovery, a plain duplicate has no spike to
                       recover — so deselecting it is the right advice. */
                    <>
                      {unusableTypes.join(', ')} carr{unusableTypes.length === 1 ? 'ies' : 'y'} no{' '}
                      {valueShort(dropped.field)} at all. Deselect{' '}
                      {unusableTypes.length === 1 ? 'it' : 'them'} or choose a QC role whose results carry
                      that value.
                    </>
                  ) : (
                    /* The type IS chartable and most of its results plotted; only
                       these particular ones carry no value — typically reported
                       below the limit. Telling the user to deselect the type here
                       would throw away every good point with it. */
                    <>
                      {dropped.types.join(', ')} results with no {valueShort(dropped.field)} recorded —
                      usually reported below the limit. They stay in the QC record but cannot be plotted;
                      the {rows.length.toLocaleString()} remaining result(s) are charted.
                    </>
                  )}
                </div>
              ) : null}
              <AppDataGrid<QcRow>
                rows={rows}
                columns={resultColumns}
                getRowId={(r, i) => r.oid ?? `${r.batch}-${r.p}-${r.qt}-${i}`}
                emptyText="No data to display"
                persistKey="control-chart-results"
              />
            </div>

            <div className="cc-card cc-card-flex">
              <div className="cc-card-t">
                Statistics by parameter
                <span className="cc-note">mean ± 2SD warning · ± 3SD control</span>
              </div>
              <AppDataGrid<Stat>
                rows={stats}
                columns={statColumns}
                getRowId={s => s.parameter}
                emptyText="No data to display"
                persistKey="control-chart-stats"
              />
            </div>
          </div>
        </div>
      ) : (
        <ChartTab
          stats={stats} st={st} pts={pts} rows={rows} field={field} theme={theme} th={th}
          overlay={overlay} overlaySeries={overlaySeries} hidden={hidden} visible={visible}
          chartIdx={chartIdx} qcTypes={qcTypes} signature={signature}
          stale={!!signature && signature.hash !== curHash()}
          statColumns={statColumns}
          onMode={setChartMode} onTheme={setTheme} onIdx={setChartIdx}
          onToggleSeries={n => setHidden(h => h.includes(n) ? h.filter(x => x !== n) : [...h, n])}
          onAllSeries={on => setHidden(on ? [] : stats.map(s => s.parameter))}
          onExport={doChartExport}
          onPopout={doPopout}
        />
      )}

      {signOpen ? (
        <SignDialog
          test={test} qcTypes={qcTypes} from={from} to={to}
          nParams={stats.length} nRows={rows.length}
          onCancel={() => setSignOpen(false)}
          onSign={doSave}
        />
      ) : null}
      {guideOpen ? <GuideDialog onClose={() => setGuideOpen(false)} /> : null}
      {auditOpen !== false ? (
        <AuditDialog srcId={auditOpen} onClose={() => setAuditOpen(false)} />
      ) : null}
      {reportOpen ? (
        <ReportDialog
          test={test} rows={rows} stats={stats} field={field} from={from} to={to}
          qcTypes={qcTypes} inst={inst} anly={anly} role={role}
          signature={signature} ccid={ccid}
          onClose={() => setReportOpen(false)}
        />
      ) : null}
    </div>
  );
}

// ── the chart tab ────────────────────────────────────────────────────────────

function ChartTab(p: {
  stats: Stat[]; st: Stat | undefined; pts: Pt[]; rows: QcRow[]; field: Field;
  theme: string; th: typeof THEMES[string]; overlay: boolean; overlaySeries: OverlaySeries[];
  hidden: string[]; visible: number; chartIdx: number; qcTypes: string[];
  signature: Signature | null; stale: boolean; statColumns: AppColumn<Stat>[];
  onMode: (m: 'single' | 'overlay') => void; onTheme: (t: string) => void; onIdx: (i: number) => void;
  onToggleSeries: (n: string) => void; onAllSeries: (on: boolean) => void;
  onExport: (what: string) => void; onPopout: (print: boolean) => boolean;
}) {
  const [thOpen, setThOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [serOpen, setSerOpen] = useState(false);

  if (!p.stats.length) {
    return (
      <div className="cc-card">
        <div className="cc-none" style={{ padding: 36 }}>
          <b>Nothing charted yet.</b><br />
          Choose a test, narrow the QC types and parameters, then Load.
        </div>
      </div>
    );
  }

  const actions = (
    <span className="cc-cardact">
      <button type="button" title="Chart theme" className="cc-thbtn"
        onClick={() => { setThOpen(o => !o); setExpOpen(false); }}>
        <span className="cc-thdot" style={{ background: p.th.swatch }} />
      </button>
      {thOpen ? (
        <div className="cc-menu">
          <h5>Chart theme</h5>
          {Object.entries(THEMES).map(([k, v]) => (
            <button key={k} type="button" className={`cc-throw${p.theme === k ? ' on' : ''}`}
              onClick={() => { p.onTheme(k); setThOpen(false); }}>
              <span className="cc-thdot" style={{ background: v.swatch }} />
              <span className="cc-thnm">{v.name}</span>
              {p.theme === k ? <span className="cc-thtick">✓</span> : null}
            </button>
          ))}
        </div>
      ) : null}
      <button type="button" title="Export chart"
        onClick={() => { setExpOpen(o => !o); setThOpen(false); }}>⤴</button>
      <button type="button" title="Preview &amp; print in a new window"
        onClick={() => p.onPopout(false)}>⧉</button>
      {expOpen ? (
        <div className="cc-menu">
          <h5>Export To</h5>
          <div className="cc-menurow">
            {['PDF', 'Image', 'Excel'].map(w => (
              <button key={w} type="button" onClick={() => { setExpOpen(false); p.onExport(w); }}>{w}</button>
            ))}
          </div>
        </div>
      ) : null}
    </span>
  );

  const modes = (
    <span className="cc-lk cc-segwrap">
      <span className="cc-segbar">
        <button type="button" className={p.overlay ? '' : 'on'}
          onClick={() => { setSerOpen(false); p.onMode('single'); }}>Single</button>
        <button type="button" className={p.overlay ? 'on' : ''} onClick={() => p.onMode('overlay')}>
          Overlay {p.visible === p.stats.length ? `all (${p.stats.length})` : `(${p.visible} of ${p.stats.length})`}
        </button>
        <button type="button" className={`${p.overlay ? 'on' : ''} cc-segcv`} title="Choose parameters"
          onClick={() => { p.onMode('overlay'); setSerOpen(o => !o); setExpOpen(false); setThOpen(false); }}>▾</button>
      </span>
      {serOpen ? (
        <div className="cc-pop narrow">
          <label className="cc-crow head">
            <input type="checkbox" checked={p.visible === p.stats.length}
              onChange={e => p.onAllSeries(e.target.checked)} />
            <span>(all)</span>
            <span className="cc-mono cc-cnt">{p.stats.length}</span>
          </label>
          {p.stats.map((s, i) => (
            <label key={s.parameter} className="cc-crow">
              <input type="checkbox" checked={!p.hidden.includes(s.parameter)}
                onChange={() => p.onToggleSeries(s.parameter)} />
              <i className="cc-swat" style={{ background: p.th.pal[i % p.th.pal.length] }} />
              <span>{s.parameter}</span>
              <span className="cc-mono cc-cnt">{s.count}</span>
            </label>
          ))}
          <div className="cc-popfoot">
            <span>{p.visible} of {p.stats.length} plotted</span>
            <button type="button" className="ca-btn" onClick={() => setSerOpen(false)}>Done</button>
          </div>
        </div>
      ) : null}
    </span>
  );

  if (p.overlay) {
    const outAll = p.stats.reduce((a, s) => a + outOfControlCount(p.rows, s, p.field), 0);
    const series = p.overlaySeries.filter(s => !p.hidden.includes(s.name));
    return (
      <div className="cc-card">
        <div className="cc-cnav">
          {modes}
          <b>All parameters</b>
          <span className="cc-muted">
            · {p.visible} of {p.stats.length} series · QCType(s): {p.qcTypes.join(', ') || 'all'}
          </span>
          <span className="cc-cnav-r">
            <span className={`cc-pill ${outAll ? 'bad' : 'ok'}`}>{outAll ? `${outAll} beyond limits` : 'In control'}</span>
          </span>
          {actions}
        </div>
        <OverlayChart series={series} field={p.field} theme={p.th} />
        <div className="cc-olegend">
          <span className="cc-legttl">Series</span>
          {p.overlaySeries.map(s => (
            <button key={s.name} type="button"
              className={`cc-lgchip${p.hidden.includes(s.name) ? ' off' : ''}`}
              onClick={() => p.onToggleSeries(s.name)}
              title={`Show or hide ${s.name}`}>
              <i style={{ background: s.col }} />{s.name}
            </button>
          ))}
          <span className="cc-legact">
            <button type="button" onClick={() => p.onAllSeries(true)}>Select all</button>
            <button type="button" onClick={() => p.onAllSeries(false)}>Clear</button>
          </span>
        </div>
        <div className="cc-overnote">
          Standardised to <b>z-scores</b> — (value − mean) ÷ SD for each parameter — so every series shares one
          axis and the limits are ±2SD and ±3SD for all of them. Tick a series off to take it out of the plot;
          hover any point for its real value.
        </div>
        <AppDataGrid<Stat> rows={p.stats} columns={p.statColumns} getRowId={s => s.parameter}
          emptyText="No data to display" persistKey="control-chart-overlay-stats" />
        <SigStrip sig={p.signature} stale={p.stale} />
      </div>
    );
  }

  const s = p.st!;
  const out = outOfControlCount(p.rows, s, p.field);
  const outWord = s.kind === 'attribute'
    ? (out === 1 ? '1 blank with growth' : `${out} blanks with growth`)
    : `${out} beyond limits`;

  return (
    <div className="cc-card">
      <div className="cc-cnav">
        {modes}
        <button type="button" className="cc-navarrow" title="Previous parameter"
          disabled={p.chartIdx === 0} onClick={() => p.onIdx(p.chartIdx - 1)}>‹</button>
        <button type="button" className="cc-navarrow" title="Next parameter"
          disabled={p.chartIdx >= p.stats.length - 1} onClick={() => p.onIdx(p.chartIdx + 1)}>›</button>
        <b>Parameter : {s.parameter}</b>
        <span className="cc-muted">
          · {p.chartIdx + 1} of {p.stats.length} · QCType(s): {p.qcTypes.join(', ') || 'all'}
        </span>
        <span className="cc-cnav-r">
          <span className={`cc-pill ${out ? 'bad' : 'ok'}`}>{out ? outWord : 'In control'}</span>
        </span>
        {actions}
      </div>

      <StatStrip st={s} />
      <LjChart pts={p.pts} st={s} field={p.field} theme={p.th} />

      <div className="cc-ptable">
        <table className="cc-g">
          <thead>
            <tr>
              <th className="num">PointNo</th><th>Date</th>
              {s.kind === 'attribute'
                /* A sterility chart has no mean and no limits. Printing those
                   columns full of dashes implies the numbers exist and were not
                   computed. */
                ? <><th className="num">Colonies</th><th>Sterility</th><th>Criterion</th></>
                : s.kind === 'range'
                  ? <><th className="num">R</th><th className="num">R̄</th><th className="num">Criterion</th></>
                  : <>
                    <th className="num">Result</th><th className="num">Mean</th><th className="num">UCL</th>
                    <th className="num">UWL</th><th className="num">LWL</th><th className="num">LCL</th>
                  </>}
            </tr>
          </thead>
          <tbody>
            {p.pts.map((pt, i) => (
              <tr key={i}>
                <td className="num">{i + 1}</td>
                <td>{fmtLocalDate(pt.d)}</td>
                {s.kind === 'attribute' ? (
                  <>
                    <td className="num cc-mono">{pt.v}</td>
                    <td><span className={`cc-pill ${pt.v ? 'bad' : 'ok'}`}>{pt.v ? 'Growth' : 'Sterile'}</span></td>
                    <td>No growth</td>
                  </>
                ) : (
                  <>
                    <td className="num cc-mono">{pt.v}</td>
                    <td className="num cc-mono">{sig(s.mean)}</td>
                    <td className="num cc-mono">{sig(s.ucl)}</td>
                    {s.kind === 'range' ? null : (
                      <>
                        <td className="num cc-mono">{sig(s.uwl)}</td>
                        <td className="num cc-mono">{sig(s.lwl)}</td>
                        <td className="num cc-mono">{sig(s.lcl)}</td>
                      </>
                    )}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="cc-gfoot"><span>{p.pts.length} point{p.pts.length === 1 ? '' : 's'}</span></div>
      <SigStrip sig={p.signature} stale={p.stale} />
    </div>
  );
}

/** The headline numbers above the plot. Four different templates because the
 *  four chart kinds do not share statistics — a sterility chart has no mean, and
 *  printing one would invent a number. */
function StatStrip({ st }: { st: Stat }) {
  if (st.kind === 'attribute') {
    return (
      <div className="cc-statstrip" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
        <div className="g"><span className="k">Blanks passed</span><span className="v">{st.pass} of {st.count}</span></div>
        <div className="r"><span className="k">Blanks with growth</span><span className="v">{st.fail}</span></div>
        <div><span className="k">Contamination rate</span><span className="v">{((st.rate ?? 0) * 100).toFixed(1)} %</span></div>
        <div className="g"><span className="k">Criterion</span><span className="v">0</span></div>
      </div>
    );
  }
  if (st.kind === 'individuals') {
    return (
      <div className="cc-statstrip" style={{ gridTemplateColumns: 'repeat(6,1fr)' }}>
        <div><span className="k">Mean log₁₀</span><span className="v">{sig(st.mean)}</span></div>
        <div><span className="k">Mean count</span><span className="v">{st.meanCount}</span></div>
        <div><span className="k">SD (log)</span><span className="v">{sig(st.std)}</span></div>
        <div className="r"><span className="k">UCL / LCL</span><span className="v">{sig(st.ucl)} / {sig(st.lcl)}</span></div>
        <div className="g"><span className="k">UWL / LWL</span><span className="v">{sig(st.uwl)} / {sig(st.lwl)}</span></div>
        <div><span className="k">Points</span><span className="v">{st.count}</span></div>
      </div>
    );
  }
  if (st.kind === 'range') {
    return (
      <div className="cc-statstrip" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
        <div><span className="k">Mean range R̄</span><span className="v">{sig(st.mean)}</span></div>
        <div className="r"><span className="k">Criterion 3.27 R̄</span><span className="v">{sig(st.ucl)}</span></div>
        <div><span className="k">Largest R</span><span className="v">{sig(st.maxR)}</span></div>
        <div className="g"><span className="k">Lower limit</span><span className="v">0</span></div>
        <div><span className="k">Pairs</span><span className="v">{st.count}</span></div>
      </div>
    );
  }
  return (
    <div className="cc-statstrip">
      <div><span className="k">Mean</span><span className="v">{sig(st.mean)}</span></div>
      <div><span className="k">SD</span><span className="v">{sig(st.std)}</span></div>
      <div className="r"><span className="k">UCL</span><span className="v">{sig(st.ucl)}</span></div>
      <div className="g"><span className="k">UWL</span><span className="v">{sig(st.uwl)}</span></div>
      <div className="g"><span className="k">LWL</span><span className="v">{sig(st.lwl)}</span></div>
      <div className="r"><span className="k">LCL</span><span className="v">{sig(st.lcl)}</span></div>
      <div><span className="k">Points</span><span className="v">{st.count}</span></div>
    </div>
  );
}

/** The signature block that travels with the chart — on screen and, when the
 *  report is printed, onto the QC record. */
function SigStrip({ sig: s, stale }: { sig: Signature | null; stale: boolean }) {
  if (!s) return null;
  return (
    <div className={`cc-sigstrip${stale ? ' stale' : ''}`}>
      <span className="cc-sgico">✎</span>
      <div className="cc-sgmain">
        <div><b>Electronically signed</b> by {s.by} · {s.role} · {s.uid} <span className="cc-muted">on {s.at}</span></div>
        <div className="cc-muted">{s.meaning}{s.comment ? ` — ${s.comment}` : ''}</div>
      </div>
      <span className="cc-sghash" title="Fingerprint of the signed data set">{s.hash}</span>
      {stale
        ? <span className="cc-pill bad">Data changed since signing</span>
        : <span className="cc-pill ok">Valid</span>}
    </div>
  );
}

// ── pickers ──────────────────────────────────────────────────────────────────

/** Instrument / Analyst multi-select. Stays open while several boxes are ticked
 *  — closing on change would make picking three analysts a three-click-reopen
 *  chore — so it carries an explicit Done. Each option shows how many QC records
 *  it holds, straight from the server's facet counts. */
function MultiPick({ label, options, values, onChange, open, setOpen, disabled, allLabel }: {
  label: string; options: { value: string; count: number }[]; values: string[];
  onChange: (v: string[]) => void;
  open: boolean; setOpen: (o: boolean) => void; disabled: boolean; allLabel: string;
}) {
  const text = values.length === 0 ? allLabel : values.length === 1 ? values[0]! : `${values.length} selected`;
  return (
    <div className="cc-frow">
      <label>{label}</label>
      <div className="cc-lk">
        <button type="button" className="cc-lkb" disabled={disabled} onClick={() => setOpen(!open)}>
          <span className={values.length ? '' : 'cc-ph'}>{text}</span>
          <i className="ti ti-chevron-down" />
        </button>
        {open ? (
          <div className="cc-pop narrow">
            <label className="cc-crow head">
              <input type="checkbox" checked={options.length > 0 && values.length === options.length}
                onChange={e => onChange(e.target.checked ? options.map(o => o.value) : [])} />
              <span>(all)</span>
            </label>
            {options.length ? options.map(o => (
              <label key={o.value} className="cc-crow">
                <input type="checkbox" checked={values.includes(o.value)}
                  onChange={() => onChange(values.includes(o.value)
                    ? values.filter(x => x !== o.value)
                    : [...values, o.value])} />
                <span>{o.value}</span>
                <span className="cc-mono cc-cnt">{o.count.toLocaleString()}</span>
              </label>
            )) : <div className="cc-none">No data to display</div>}
            <div className="cc-popfoot">
              <span>{values.length} selected</span>
              <button type="button" className="ca-btn" onClick={() => setOpen(false)}>Done</button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** A self-contained bordered picker with its own footer — the QC Type and
 *  Parameter grids. Every row is visible (no inner scroll) so the choice can be
 *  made without hunting. */
function CheckGrid({ cols, template, rows, sel, onToggle, onAll }: {
  cols: string[]; template: string;
  rows: { id: string; cells: string[]; n: number }[];
  sel: string[]; onToggle: (id: string) => void; onAll: (on: boolean) => void;
}) {
  const all = rows.length > 0 && sel.length === rows.length;
  return (
    <div className="cc-subgrid">
      <div className="cc-sgh" style={{ gridTemplateColumns: template }}>
        <span className="cc-cb">
          <input type="checkbox" checked={all} disabled={!rows.length}
            onChange={e => onAll(e.target.checked)} aria-label="Select all" />
        </span>
        {cols.map(c => <span key={c}>{c}</span>)}
        <span className="r">Records</span>
      </div>
      <div className="cc-sgb">
        {rows.length ? rows.map(r => (
          <label key={r.id} className={`cc-sgr${sel.includes(r.id) ? ' on' : ''}`}
            style={{ gridTemplateColumns: template }}>
            <span className="cc-cb">
              <input type="checkbox" checked={sel.includes(r.id)} onChange={() => onToggle(r.id)} />
            </span>
            {r.cells.map((c, i) => <span key={i}>{c}</span>)}
            <span className="r cc-mono">{r.n.toLocaleString()}</span>
          </label>
        )) : <div className="cc-none">No data to display</div>}
      </div>
      <div className="cc-sgf">
        <span>Page 1 of 1 ({rows.length} item{rows.length === 1 ? '' : 's'})</span>
      </div>
    </div>
  );
}

// ── dialogs ──────────────────────────────────────────────────────────────────

function Modal({ title, icon, wide, children, footer, onClose }: {
  title: string; icon: string; wide?: boolean;
  children: React.ReactNode; footer: React.ReactNode; onClose: () => void;
}) {
  return (
    <div className="cc-scrim" onClick={e => { if (e.target === e.currentTarget) onClose(); }} role="presentation">
      <div className={`cc-dlg${wide ? ' lg' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="cc-dt"><span className="cc-dic">{icon}</span>{title}</div>
        <div className="cc-db">{children}</div>
        <div className="cc-df">{footer}</div>
      </div>
    </div>
  );
}

/** Electronic signature (ADR-0009). Three parts: something the signer KNOWS, a
 *  SECOND factor, and an explicit ATTESTATION carrying the meaning — 21 CFR
 *  Part 11 §11.50 wants the printed name, the timestamp and the MEANING stored
 *  with the record, which is why the meaning is a chosen value, not free text.
 *
 *  The password and the code are verified by the API against the login seam and
 *  the signer's signing authenticator; they are sent once and never persisted.
 *  A rejected credential keeps the dialog OPEN with the server's message, so the
 *  user can retype rather than rebuild the whole chart. */
function SignDialog({ test, qcTypes, from, to, nParams, nRows, onCancel, onSign }: {
  test: TestDef | null; qcTypes: string[]; from: string; to: string;
  nParams: number; nRows: number;
  onCancel: () => void;
  onSign: (s: { meaning: string; comment: string; password: string; code: string }) => Promise<string | null>;
}) {
  const [meaning, setMeaning] = useState(SIG_MEANINGS[0]!);
  const [pw, setPw] = useState('');
  const [otp, setOtp] = useState('');
  const [comment, setComment] = useState('');
  const [attest, setAttest] = useState(false);
  const [err, setErr] = useState('');
  const [serverErr, setServerErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!pw) return setErr('pw');
    if (!/^\d{6}$/.test(otp)) return setErr('otp');
    if (!attest) return setErr('att');
    setBusy(true);
    setErr(''); setServerErr('');
    const msg = await onSign({ meaning, comment: comment.trim(), password: pw, code: otp });
    setBusy(false);
    if (msg) { setErr('bad'); setServerErr(msg); }
  };

  return (
    <Modal title="Electronic signature" icon="✎" onClose={onCancel}
      footer={<>
        <button type="button" className="ca-btn" onClick={onCancel} disabled={busy}>Cancel</button>
        <button type="button" className="ca-btn ca-btn-solid" onClick={() => void submit()} disabled={busy}>
          {busy ? 'Signing…' : 'Sign & Save'}
        </button>
      </>}>
      <div className="cc-sigwhat">
        You are signing <b>{test?.test}</b> · {test?.method} · {qcTypes.join(', ') || 'all QC types'} ·{' '}
        {fmtLocalDate(from)} – {fmtLocalDate(to)} · <b>{nParams}</b> parameter(s) over{' '}
        <b>{nRows.toLocaleString()}</b> results.
      </div>

      <div className="cc-sigrow">
        <label htmlFor="sig-mean">Meaning <span className="cc-star">*</span></label>
        <select id="sig-mean" className="cc-in" value={meaning} onChange={e => setMeaning(e.target.value)}>
          {SIG_MEANINGS.map(m => <option key={m}>{m}</option>)}
        </select>
      </div>
      <div className="cc-sigrow">
        <label htmlFor="sig-pw">Password <span className="cc-star">*</span></label>
        <input id="sig-pw" type="password" autoComplete="off" className={`cc-in req${err === 'pw' || err === 'bad' ? ' bad' : ''}`}
          placeholder="Your account password" value={pw}
          onChange={e => { setPw(e.target.value); setErr(''); setServerErr(''); }} />
      </div>
      <div className="cc-sigrow">
        <label htmlFor="sig-otp">Second factor <span className="cc-star">*</span></label>
        <input id="sig-otp" inputMode="numeric" maxLength={6} className={`cc-in req${err === 'otp' ? ' bad' : ''}`}
          placeholder="6-digit code from your authenticator" value={otp}
          onChange={e => { setOtp(e.target.value); setErr(''); setServerErr(''); }} />
      </div>
      <div className="cc-sigrow">
        <label htmlFor="sig-cmt">Comment</label>
        <input id="sig-cmt" className="cc-in" placeholder="Optional — recorded with the signature"
          value={comment} onChange={e => setComment(e.target.value)} />
      </div>
      <label className={`cc-sigatt${err === 'att' ? ' bad' : ''}`}>
        <input type="checkbox" checked={attest} onChange={e => { setAttest(e.target.checked); setErr(''); }} />
        <span>
          I understand that this electronic signature is the legal equivalent of my handwritten signature,
          and that it is applied to the data set described above.
        </span>
      </label>
      {err ? <div className="cc-sigerr">{serverErr || SIG_ERRS[err]}</div> : null}
    </Modal>
  );
}

/** The audit trail, from the shared /api/v1/audit/trail endpoint — the same rows
 *  Data Center → Audit Trail Log shows. Written by IAuditTrailService, never by
 *  this form (§14a Rule A). */
function AuditDialog({ srcId, onClose }: { srcId: string | null; onClose: () => void }) {
  const [list, setList] = useState<ApiAuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true); setErr('');
    fetchAudit(srcId, ac.signal)
      .then(r => { if (!ac.signal.aborted) setList(r); })
      .catch(e => { if (!ac.signal.aborted) setErr(apiMessage(e, 'The audit trail could not be loaded.')); })
      .finally(() => { if (!ac.signal.aborted) setLoading(false); });
    return () => ac.abort();
  }, [srcId]);

  return (
    <Modal title="Audit Trail" icon="🛡" wide onClose={onClose}
      footer={<button type="button" className="ca-btn ca-btn-solid" onClick={onClose}>Close</button>}>
      <div className="cc-autop">
        {srcId ? <>Chart <b>{srcId}</b></> : 'All control-chart activity'} · <b>{list.length}</b>{' '}
        entr{list.length === 1 ? 'y' : 'ies'}
        <span className="cc-note">Written by IAuditTrailService — never by a form</span>
      </div>
      <div className="cc-augrid">
        {loading ? <div className="cc-none">Loading…</div>
          : err ? <div className="cc-none">{err}</div>
            : list.length ? (
              <table className="cc-g">
                <thead>
                  <tr>
                    <th>When</th><th>Operation</th><th>Form</th><th>Record</th><th>Property</th>
                    <th>Old value</th><th>New value</th><th>User</th><th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(a => (
                    <tr key={a.oid}>
                      <td className="cc-mono">{a.createdDate ?? '—'}</td>
                      <td><span className={`cc-pill ${OP_TONE[a.operationType ?? ''] || ''}`}>{a.operationType ?? '—'}</span></td>
                      <td>{a.formName ?? '—'}</td>
                      <td className="cc-mono">{a.sourceId || '—'}</td>
                      <td>{a.propertyName ?? '—'}</td>
                      <td className="cc-muted">{a.oldValue || '—'}</td>
                      <td><b>{a.newValue || '—'}</b></td>
                      <td>{a.createdBy ?? '—'}</td>
                      <td>{a.comment || a.reasonForChange || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="cc-none">No audit entries for this record yet.</div>}
      </div>
    </Modal>
  );
}

/** The Guide is a mini SOP, not a tooltip: it states the statistic, the
 *  acceptance rules and the deviations, so an analyst can justify a chart to an
 *  auditor without leaving the screen. Anything that is lab POLICY rather than a
 *  published requirement is labelled as such. */
function GuideDialog({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="Control Chart — how it works (mini SOP)" icon="📄" wide onClose={onClose}
      footer={<button type="button" className="ca-btn ca-btn-solid" onClick={onClose}>Close</button>}>
      <div className="cc-sop">
        <h4>1 · Purpose</h4>
        <p>
          Monitor analytical <b>accuracy</b> (spike recovery) and <b>precision</b> (duplicate agreement) over
          time, so a drifting method, instrument or analyst is caught before it reaches a report.
        </p>

        <h4>2 · What each chart plots</h4>
        <table>
          <tbody>
            <tr><td><b>% Recovery</b></td><td>Spike and standard QC (LCS, MS, LOQ, CCV, ICV) — accuracy. Individuals chart, mean ± 2SD warning, ± 3SD control.</td></tr>
            <tr><td><b>RPD</b></td><td>Duplicates (Dup, LCSD, MSD) — precision. Lower limits floored at zero: an RPD cannot be negative.</td></tr>
            <tr><td><b>Result</b></td><td>Blanks (Method Blank, CCB, Trip Blank) — contamination. A blank has no recovery, so its numeric result is charted.</td></tr>
            <tr><td><b>Range (R)</b></td><td>Microbiological duplicates — R = |log₁₀C₁ − log₁₀C₂|, centred on R̄ with the single criterion 3.27 R̄ (SM 9020 B, D4 for n = 2). No warning band, no lower limit.</td></tr>
            <tr><td><b>Colonies</b></td><td>Negative control — sterility. An attribute, not a measurement: the criterion is zero growth, so there is no mean and no SD.</td></tr>
          </tbody>
        </table>

        <h4>3 · Limits</h4>
        <p>
          Computed by the API from the data in the selected window using the <b>sample</b> standard deviation
          (n − 1). Warning = mean ± 2SD, control = mean ± 3SD. Limits are recalculated whenever the window or
          the QC type selection changes — which is why a signed chart is fingerprinted.
        </p>

        <h4>4 · Acceptance rules</h4>
        <table>
          <tbody>
            {RULE_TEXT.map(([r, txt]) => (
              <tr key={r}><td><b>{r}</b></td><td>{txt}</td></tr>
            ))}
          </tbody>
        </table>

        <h4>5 · Disposition</h4>
        <p>
          A rejection rule stops the batch; a run rule opens an investigation. The disposition is recorded with
          the excursion so the report is an auditable record rather than an observation.
        </p>

        <h4>6 · Signature</h4>
        <p>
          A saved chart fixes the limits routine QC is judged against, so it is <b>signed</b>, not merely saved:
          password, second factor and an explicit attestation, with the meaning stored alongside (21 CFR Part 11
          §11.50). The data set is fingerprinted at signing; re-query it over a different window and the
          signature reads as <b>stale</b> rather than appearing to cover data it never saw.
        </p>

        <h4>7 · What a saved chart owns</h4>
        <p>
          Saving CLAIMS the QC results it charts: each one belongs to exactly one chart, so the same point can
          never be counted twice across two sets of limits. Deleting a chart releases them again.
        </p>
      </div>
    </Modal>
  );
}
